import useActions from '@app/store/hooks/useActions';
import { useTypedDispatch } from '@app/store/hooks/useTypedDispatch';

import { usePatchRoutingConfigVersionMetadataMutation } from '../api';
import { GENERIC_METADATA_ERROR, parseConfigPathErrors } from '../lib/configErrors';
import { applyVersionToCaches } from '../lib/versionCache';
import { IVersionMetadataInput, toMetadataBody } from '../lib/versionMetadata';
import { IConfigIssue, IRoutingConfigVersion } from '../model/types';

export type TEditVersionMetadataResult =
  | { status: 'updated'; version: IRoutingConfigVersion }
  | { status: 'failed'; issues: IConfigIssue[] };

// One metadata PATCH, any status: no draft, no publish. The caches are patched in
// place, and a live edit session follows the bumped updated_at.
export const useEditVersionMetadata = () => {
  const dispatch = useTypedDispatch();
  const { syncSessionBase } = useActions();
  const [patchMetadata, { isLoading }] = usePatchRoutingConfigVersionMetadataMutation();

  const editVersionMetadata = async (
    versionId: number,
    input: Partial<IVersionMetadataInput>,
  ): Promise<TEditVersionMetadataResult> => {
    try {
      const response = await patchMetadata({ id: versionId, body: toMetadataBody(input) }).unwrap();

      syncSessionBase(response.data);
      dispatch(applyVersionToCaches(response));

      return { status: 'updated', version: response.data };
    } catch (error) {
      return { status: 'failed', issues: parseConfigPathErrors(error, GENERIC_METADATA_ERROR) };
    }
  };

  return { editVersionMetadata, isUpdating: isLoading };
};
